import { useEffect } from "react";
import Reveal from "@/components/Reveal";

const SECTIONS = [
  {
    label: "— What we collect",
    body: "When you submit a request for simulator access, we collect your name, email address, institutional affiliation, and the message you provide. We also log anonymous session identifiers — randomly generated, stored in a browser cookie — to count unique visitors and page views on qpilabs.ai. We do not collect any other personal information.",
  },
  {
    label: "— How we use it",
    body: "Access request data is used solely to evaluate and process your request for simulator access. We do not use it for marketing, profiling, or any automated decision-making. Anonymous session data is used internally to understand aggregate visitor numbers. It is never tied to your identity.",
  },
  {
    label: "— Who we share it with",
    body: "Nobody. We do not sell, rent, share, or transfer your personal information to any third party. We do not use Google Analytics, Meta Pixel, or any third-party advertising or tracking services.",
  },
  {
    label: "— Cookies",
    body: "We use a single session cookie to count unique visitors to qpilabs.ai. This cookie contains a randomly generated identifier with no connection to your name, email, or device identity. It expires when you close your browser. We do not use tracking cookies, advertising cookies, or persistent fingerprinting of any kind.",
  },
  {
    label: "— Do Not Track",
    body: "Some browsers send a Do Not Track (DNT) signal. Our anonymous session cookie does not track you across sites and contains no personally identifiable information, so we do not alter its behaviour based on DNT signals. No behavioural or cross-site tracking occurs regardless of your DNT setting.",
  },
  {
    label: "— Your Rights & Contact",
    body: "You may request access to, correction of, or deletion of any personal information you submitted via the access request form. To do so, email us at info@qpilabs.ai. We will respond within 30 days. This policy may be updated periodically — the effective date above reflects the most recent revision.",
  },
];

export default function Privacy() {
  useEffect(() => {
    document.title = "Privacy Policy · QPI Labs";
  }, []);

  return (
    <main className="pt-16" data-testid="privacy-page">
      <section className="border-b border-white/10 py-20 md:py-28">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow mb-6 text-laser">— Legal</p>
            <h1 className="max-w-4xl text-5xl font-extrabold tracking-tighter md:text-7xl">
              Privacy Policy
            </h1>
            <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-zinc-500">
              Effective October 2026 · QPI Laboratories, Inc. · California, United States
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto w-full max-w-[720px] px-6">
          {SECTIONS.map((section, i) => (
            <Reveal key={section.label}>
              <article
                className="border-t border-white/10 py-14 first:border-t-0 first:pt-0 md:py-16"
                data-testid={`privacy-section-${i + 1}`}
              >
                <p className="eyebrow mb-5">{section.label}</p>
                <p className="font-mono text-[13px] leading-relaxed text-zinc-400">
                  {section.body.includes("info@qpilabs.ai") ? (
                    <>
                      {section.body.split("info@qpilabs.ai")[0]}
                      <a
                        href="mailto:info@qpilabs.ai"
                        data-testid="privacy-contact-link"
                        className="text-beam transition-colors hover:underline"
                      >
                        info@qpilabs.ai
                      </a>
                      {section.body.split("info@qpilabs.ai")[1]}
                    </>
                  ) : (
                    section.body
                  )}
                </p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
